import { cn } from "@util/tailwind";
import { ReactNode } from "react";

// Main Layout Component
interface TwoPanelContentLayoutProps {
    leftPanel: ReactNode;
    rightPanel: ReactNode;
    className?: string;
}

export function TwoPanelContentLayout({
    leftPanel,
    rightPanel,
    className,
}: TwoPanelContentLayoutProps) {
    return (
        <div
            className={cn(
                "gap-6 h-full",
                className, {
                "grid grid-cols-1 lg:grid-cols-[400px_1fr]": leftPanel,
            }
            )}
        >
            <div
                className="space-y-4 overflow-y-auto"
                style={{ paddingRight: "8px", paddingLeft: "8px" }}
            >
                {leftPanel}
            </div>
            <div className="space-y-4 overflow-y-auto">{rightPanel}</div>
        </div>
    );
}

// Left Panel Components
interface LeftPanelHeaderProps {
    title: string;
    description?: string;
    action?: ReactNode;
    className?: string;
}

export function LeftPanelHeader({
    title,
    description,
    action,
    className,
}: LeftPanelHeaderProps) {
    return (
        <div className={cn("space-y-3", className)}>
            <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {title}
                </h3>
                {description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {description}
                    </p>
                )}
            </div>
            {action && <div>{action}</div>}
        </div>
    );
}

interface LeftPanelSectionProps {
    label: string;
    children: ReactNode;
    className?: string;
}

export function LeftPanelSection({
    label,
    children,
    className,
}: LeftPanelSectionProps) {
    return (
        <div className={cn("space-y-2", className)}>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                {label}
            </label>
            {children}
        </div>
    );
}

interface LeftPanelListProps {
    children: ReactNode;
    className?: string;
}

export function LeftPanelList({ children, className }: LeftPanelListProps) {
    return (
        <div className={cn("flex-1 overflow-y-auto space-y-2", className)}>
            {children}
        </div>
    );
}

// Right Panel Components
interface RightPanelHeaderProps {
    title: string;
    subtitle?: string;
    actions?: ReactNode;
    className?: string;
}

export function RightPanelHeader({
    title,
    subtitle,
    actions,
    className,
}: RightPanelHeaderProps) {
    return (
        <div
            className={cn(
                "flex items-start justify-between pb-5 border-b border-gray-200 dark:border-gray-700",
                className
            )}
        >
            <div className="flex-1 space-y-1">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    {title}
                </h3>
                {subtitle && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
                )}
            </div>
            {actions && <div className="flex gap-2 ml-6">{actions}</div>}
        </div>
    );
}

interface RightPanelSectionProps {
    label: string;
    description?: string;
    children: ReactNode;
    className?: string;
}

export function RightPanelSection({
    label,
    description,
    children,
    className,
}: RightPanelSectionProps) {
    return (
        <div className={cn("space-y-2", className)}>
            <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                    {label}
                </label>
                {description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {description}
                    </p>
                )}
            </div>
            {children}
        </div>
    );
}

interface RightPanelContentProps {
    children: ReactNode;
    className?: string;
}

export function RightPanelContent({
    children,
    className,
}: RightPanelContentProps) {
    return <div className={cn("space-y-6", className)}>{children}</div>;
}
